/**
 * Mô phỏng chuyển động theo `salonType`.
 *  - Hoạt náo MỌI geometry (box / cylinder / extrusion / mesh nhúng) qua AABB tổng quát:
 *    tâm = partCenter, kích thước = bounding-box của geometry (đã căn gốc). Nhờ vậy tay nắm
 *    (extrusion) gắn trên ngăn kéo / cánh cũng chuyển động.
 *  - KHUNG CHUNG theo nhóm (drawerGroup): mọi part trong nhóm xoay quanh CÙNG bản lề / tịnh
 *    tiến cùng vector (suy từ tấm "đại diện" = box lớn nhất nhóm) → cánh + tay nắm dính nhau.
 *  - update: lerp `t += (target - t) * 0.07`, áp transform theo salonType.
 * Khi nhóm chỉ có 1 box, mọi công thức giảm đúng về thuật toán cũ đã kiểm chứng.
 * Tham chiếu: design/02-wvm-schema.md §2.4.
 */
import { Quaternion, Vector3 } from 'three';
import { SCALE } from './constants';
import {
  isBox,
  isMeshInline,
  type SalonType,
  type WvmPart,
} from '../lib/types/wvm';
import { partCenter } from './MeshBuilder';
import type { PartObject } from './SceneManager';

const S = SCALE;
const SETTLE = 0.001; // ngưỡng coi như đã tới đích

interface AnimState {
  t: number;
  target: number;
}

/** AABB world (mm): tâm cx/cy/cz + kích thước wx/hy/dz — chuẩn hoá mọi kind về "khối hộp". */
interface BoxLike {
  cx: number;
  cy: number;
  cz: number;
  wx: number;
  hy: number;
  dz: number;
}

/** Khung chuyển động dùng chung cho cả nhóm (suy từ tấm đại diện) — tính 1 lần, cache. */
interface Frame {
  ref: BoxLike; // tấm đại diện (mm)
  st: SalonType;
  fd: number; // flip → ±1
  rot: { axis: 'x' | 'y' | 'z'; angle: number } | null; // góc đầy đủ tại t=1 (apply ×t)
  hinge: { x: number; y: number; z: number }; // điểm bản lề (scene units)
  off: { x: number; y: number; z: number } | null; // vector tịnh tiến đầy đủ tại t=1 (scene units)
  // Xoay quanh TRỤC WORLD tuỳ ý (motion.rotate): trục đơn vị + góc rad đầy đủ tại t=1. hinge = pivot (scene units).
  quat?: { ax: number; ay: number; az: number; angle: number } | null;
  // "Tay nâng Blum" (Aventos HF gập đôi) — CÁNH DƯỚI: bản lề giữa DI ĐỘNG theo cánh trên.
  // pivot(t) = H_M0 + R_A(angleUp·t)·v0 − v0, v0 = (0,−Lup,0) (scene). quat.angle = góc cánh dưới (= −angleUp).
  bi?: {
    ax: number; ay: number; az: number; // trục bản lề (đơn vị world)
    angleUp: number; // góc đầy đủ của cánh TRÊN (rad) — để suy quỹ đạo bản lề giữa
    hm0x: number; hm0y: number; hm0z: number; // bản lề giữa lúc đóng (scene = mép trên cánh dưới)
    Lup: number; // chiều dài cánh trên (scene) — bán kính quét của bản lề giữa
  } | null;
}

/** Có chuyển động (salonType≠0 HOẶC slideVec tường minh) trên part DỰNG ĐƯỢC (mesh-uri chưa dựng → loại). */
export function isAnimatable(part: WvmPart): boolean {
  if (!part.motion?.salonType && !part.motion?.slideVec && !part.motion?.rotate) return false;
  const g = part.geometry;
  return g.kind !== 'mesh' || isMeshInline(g);
}

/** Trục + góc xoay theo salonType (góc đầy đủ; apply nhân t). isXt = mỏng nhất theo X. */
const ROT: Partial<Record<number, (isXt: boolean, fd: number) => { axis: 'x' | 'y' | 'z'; angle: number }>> = {
  2: (_x, fd) => ({ axis: 'y', angle: Math.PI * 0.52 * fd }),
  3: (_x, fd) => ({ axis: 'y', angle: -Math.PI * 0.52 * fd }),
  4: (x, fd) => ({ axis: x ? 'z' : 'x', angle: Math.PI * 0.5 * fd }),
  5: (x, fd) => ({ axis: x ? 'z' : 'x', angle: -Math.PI * 0.48 * fd }),
  12: (x, fd) => ({ axis: x ? 'z' : 'x', angle: -Math.PI * 0.67 * fd }),
  14: (x, fd) => ({ axis: x ? 'z' : 'x', angle: -Math.PI * 0.5 * fd }),
};

/** Điểm bản lề (mm) trên tấm đại diện theo salonType — port từ setupPivot cũ. */
function hingePoint(r: BoxLike, st: SalonType, fd: number): { x: number; y: number; z: number } {
  const hwx = r.wx / 2;
  const hhy = r.hy / 2;
  const hdz = r.dz / 2;
  const minD = Math.min(r.wx, r.hy, r.dz);
  const isZt = r.dz === minD;
  const isXt = r.wx === minD;
  let x = r.cx;
  let y = r.cy;
  let z = r.cz;
  if (st === 2 || st === 3) {
    // bản lề đứng: cạnh trước/sau (Z) nếu mặt hướng X, ngược lại cạnh trái/phải (X)
    if (isXt) z = r.cz + (st === 2 ? hdz : -hdz) * fd;
    else x = r.cx + (st === 2 ? hwx : -hwx) * fd;
  } else if (st === 4) {
    y = r.cy - hhy; // bản lề đáy
  } else if (st === 5) {
    y = r.cy + hhy; // bản lề đỉnh
  } else if (st === 12 || st === 14) {
    y = r.cy + hhy; // aventos: bản lề trên(-sau)
    if (isZt) z = r.cz - hdz;
  }
  return { x, y, z };
}

export class MotionEngine {
  private readonly states = new Map<number, AnimState>();
  private readonly boxCache = new Map<number, BoxLike | null>(); // theo hình học → bền
  private readonly frameCache = new Map<number, Frame | null>(); // theo nhóm/motion → xoá khi reassign
  private readonly _axis = new Vector3(); // tạm cho quaternion xoay trục-bất-kỳ (tránh cấp phát mỗi frame)
  private readonly _tmpQ = new Quaternion(); // tạm cho quỹ đạo bản lề giữa (Blum HF)
  private readonly _tmpV = new Vector3();

  constructor(
    private readonly objects: PartObject[],
    private readonly requestAnimate: () => void,
  ) {
    this.setupAll();
  }

  /** AABB chuẩn hoá (mm) cho mọi kind — cache vì hình học không đổi. */
  private partBox(obj: PartObject): BoxLike | null {
    const id = obj.part.id;
    const hit = this.boxCache.get(id);
    if (hit !== undefined) return hit;
    let box: BoxLike | null = null;
    const g = obj.part.geometry;
    if (isBox(g)) {
      // box: dùng kích thước khai báo (chuẩn hơn bbox khi có bo góc/cutout)
      box = { cx: g.cx, cy: g.cy, cz: g.cz, wx: g.wx, hy: g.hy, dz: g.dz };
    } else {
      // non-box: geometry đã căn gốc → bbox cho kích thước; tâm world từ partCenter
      const geo = obj.mesh.geometry;
      if (!geo.boundingBox) geo.computeBoundingBox();
      const bb = geo.boundingBox;
      if (bb) {
        const [cx, cy, cz] = partCenter(obj.part); // scene units
        box = {
          cx: cx / S,
          cy: cy / S,
          cz: cz / S,
          wx: (bb.max.x - bb.min.x) / S,
          hy: (bb.max.y - bb.min.y) / S,
          dz: (bb.max.z - bb.min.z) / S,
        };
      }
    }
    this.boxCache.set(id, box);
    return box;
  }

  /** Tấm đại diện của nhóm (box lớn nhất theo thể tích) — quyết định bản lề/trục chung. */
  private primary(obj: PartObject): { box: BoxLike; st: SalonType; fd: number; part: WvmPart } | null {
    const grp = obj.part.motion?.drawerGroup ?? 0;
    const members =
      grp > 0
        ? this.objects.filter((o) => o.part.motion?.drawerGroup === grp && !!o.part.motion?.salonType)
        : [obj];
    let best: { box: BoxLike; st: SalonType; fd: number; part: WvmPart } | null = null;
    let vol = -1;
    for (const o of members) {
      const b = this.partBox(o);
      if (!b) continue;
      const v = b.wx * b.hy * b.dz;
      if (v > vol) {
        vol = v;
        best = {
          box: b,
          st: (o.part.motion?.salonType ?? 0) as SalonType,
          fd: o.part.motion?.flip ? -1 : 1,
          part: o.part,
        };
      }
    }
    return best;
  }

  /** Khung chuyển động (bản lề/trục) cho part — cache theo nhóm. */
  private frame(obj: PartObject): Frame | null {
    const id = obj.part.id;
    const hit = this.frameCache.get(id);
    if (hit !== undefined) return hit;
    // "Tay nâng Blum" (Aventos HF gập đôi): 2 cánh salonType=14 xếp chồng CÙNG NHÓM → ghép cặp
    // (ưu tiên hơn cả `rotate` mà plugin có thể phát cho từng cánh). Cánh 14 đơn lẻ → rơi xuống heuristic cũ.
    if (obj.part.motion?.salonType === 14) {
      const bf = this.biFold(obj);
      if (bf) {
        this.frameCache.set(id, bf);
        return bf;
      }
    }
    // slideVec tường minh (phụ kiện đọc từ TFurnAnimation): trượt thẳng theo vector world, BỎ heuristic.
    const sv = obj.part.motion?.slideVec;
    if (sv) {
      const box = this.partBox(obj);
      const ref: BoxLike = box ?? { cx: 0, cy: 0, cz: 0, wx: 0, hy: 0, dz: 0 };
      const hinge = { x: ref.cx * S, y: ref.cy * S, z: ref.cz * S };
      const fr: Frame = { ref, st: 0 as SalonType, fd: 1, rot: null, hinge, off: { x: sv.x * S, y: sv.y * S, z: sv.z * S } };
      this.frameCache.set(id, fr);
      return fr;
    }
    // rotate tường minh (phụ kiện lật, đọc từ TFurnAnimation): xoay quanh TRỤC WORLD tuỳ ý qua pivot.
    const rt = obj.part.motion?.rotate;
    if (rt) {
      const box = this.partBox(obj);
      const ref: BoxLike = box ?? { cx: 0, cy: 0, cz: 0, wx: 0, hy: 0, dz: 0 };
      const hinge = { x: rt.pivot.x * S, y: rt.pivot.y * S, z: rt.pivot.z * S };
      const fr: Frame = {
        ref, st: 0 as SalonType, fd: 1, rot: null, hinge, off: null,
        quat: { ax: rt.axis.x, ay: rt.axis.y, az: rt.axis.z, angle: (rt.angle * Math.PI) / 180 },
      };
      this.frameCache.set(id, fr);
      return fr;
    }
    let fr: Frame | null = null;
    const prim = this.primary(obj);
    if (prim) {
      const minD = Math.min(prim.box.wx, prim.box.hy, prim.box.dz);
      const isXt = prim.box.wx === minD;
      const rotFn = ROT[prim.st];
      let rot = rotFn ? rotFn(isXt, prim.fd) : null;
      // Chiều sâu RA/VÀO (Author): cửa bản lề ĐỨNG (2/3) + swingOut → ĐẢO DẤU GÓC, GIỮ NGUYÊN bản lề
      // → cạnh tự do quét −Z thay vì +Z (tủ "quay 180°", mặt trước −Z). TRỰC GIAO với flip (trái/phải).
      // Vắng swingOut ⇒ +Z như cũ (tương thích ngược). Xem WvmMotion.swingOut / WO-04a.
      if (rot && prim.part.motion?.swingOut && (prim.st === 2 || prim.st === 3)) {
        rot = { axis: rot.axis, angle: -rot.angle };
      }
      let hinge = { x: prim.box.cx * S, y: prim.box.cy * S, z: prim.box.cz * S };
      if (rot) {
        const h = hingePoint(prim.box, prim.st, prim.fd);
        hinge = { x: h.x * S, y: h.y * S, z: h.z * S };
      }
      fr = { ref: prim.box, st: prim.st, fd: prim.fd, rot, hinge, off: null };
      if (!rot) fr.off = this.slideOffset(fr, obj); // tịnh tiến: tính 1 lần
    }
    this.frameCache.set(id, fr);
    return fr;
  }

  /**
   * "Tay nâng Blum" (Aventos HF gập đôi). Cặp 2 cánh salonType=14 CÙNG NHÓM, xếp chồng dọc (Y):
   *  - Cánh TRÊN: xoay quanh bản lề cố định ở MÉP TRÊN (nóc tủ), góc −ALPHA·sign → mép tự do hất RA.
   *  - Cánh DƯỚI: xoay quanh BẢN LỀ GIỮA (mép dưới cánh trên) đang DI ĐỘNG; gập một lượng để tạo
   *    GÓC MỞ 83° với cánh trên (mép dưới CHÌA RA, không ép trượt sát mặt tủ).
   * Trục bản lề = trục ngang ⊥ pháp tuyến mặt (mỏng nhất) và ⊥ phương đứng. `flip` đảo chiều hất ra/vào.
   * Trả null nếu không phải cặp hợp lệ (đơn lẻ / >2 / nằm ngang / lệch trục) → caller dùng heuristic cũ.
   */
  private biFold(obj: PartObject): Frame | null {
    const grp = obj.part.motion?.drawerGroup ?? 0;
    if (grp <= 0) return null; // phải gộp nhóm thủ công
    const leaves = this.objects
      .filter((o) => (o.part.motion?.drawerGroup ?? 0) === grp && o.part.motion?.salonType === 14)
      .map((o) => ({ o, b: this.partBox(o) }))
      .filter((x): x is { o: PartObject; b: BoxLike } => !!x.b);
    if (leaves.length !== 2) return null;
    leaves.sort((p, q) => q.b.cy - p.b.cy); // [0]=trên (cy cao), [1]=dưới
    const up = leaves[0];
    const lo = leaves[1];
    const thinUp = Math.min(up.b.wx, up.b.hy, up.b.dz);
    if (thinUp === up.b.hy) return null; // mỏng theo Y = tấm nằm ngang → không phải cửa lật đứng
    // xếp chồng & thẳng hàng: cùng footprint X/Z, cánh trên nằm trên cánh dưới
    const alignX = Math.abs(up.b.cx - lo.b.cx) <= Math.max(up.b.wx, lo.b.wx) * 0.5 + 5;
    const alignZ = Math.abs(up.b.cz - lo.b.cz) <= Math.max(up.b.dz, lo.b.dz) * 0.5 + 5;
    if (!alignX || !alignZ) return null;

    // Cánh TRÊN mở hết góc (~88°); cánh DƯỚI tạo với cánh trên GÓC MỞ 83° (owner chốt).
    // KHÔNG ép mép dưới trượt sát mặt tủ → cánh dưới CHÌA RA. Gập cánh dưới khỏi phương đồng phẳng
    // một lượng offset = 180°−83° = 97° (cùng phía gập của mô hình cũ) ⇒ dihedral giữa 2 cánh = 83°.
    const ALPHA = Math.PI * 0.49; // ~88°: độ mở cánh trên (như bản đầu)
    const FOLD_OPEN_DEG = 83;
    const foldOffset = ((180 - FOLD_OPEN_DEG) * Math.PI) / 180; // 97°
    const flip = up.o.part.motion?.flip ? -1 : 1;
    // trục bản lề + góc cánh trên để mép tự do quét về phía +(trục mỏng) (mặc định; flip đảo).
    const thinIsZ = thinUp === up.b.dz;
    const ax = thinIsZ ? 1 : 0;
    const az = thinIsZ ? 0 : 1;
    const angleUp = (thinIsZ ? -ALPHA : ALPHA) * flip;
    const angleLow = angleUp - Math.sign(angleUp) * foldOffset;

    const htx = up.b.cx * S;
    const hty = (up.b.cy + up.b.hy / 2) * S;
    const htz = up.b.cz * S;
    const Lup = up.b.hy * S;
    const hinge = { x: htx, y: hty, z: htz };

    if (obj === up.o) {
      // cánh trên: xoay quanh bản lề nóc (bản lề tĩnh) — dùng nhánh quaternion sẵn có
      return { ref: up.b, st: 14 as SalonType, fd: 1, rot: null, hinge, off: null, quat: { ax, ay: 0, az, angle: angleUp }, bi: null };
    }
    // cánh dưới: bản lề giữa di động
    const hm0 = { x: lo.b.cx * S, y: (lo.b.cy + lo.b.hy / 2) * S, z: lo.b.cz * S };
    return {
      ref: lo.b,
      st: 14 as SalonType,
      fd: 1,
      rot: null,
      hinge: hm0, // lúc đóng; apply() dời theo quỹ đạo mỗi frame
      off: null,
      quat: { ax, ay: 0, az, angle: angleLow },
      bi: { ax, ay: 0, az, angleUp, hm0x: hm0.x, hm0y: hm0.y, hm0z: hm0.z, Lup },
    };
  }

  /** Vector tịnh tiến (scene units, đầy đủ tại t=1) cho loại trượt — suy từ tấm đại diện. */
  private slideOffset(fr: Frame, obj: PartObject): { x: number; y: number; z: number } {
    const r = fr.ref;
    const fd = fr.fd;
    const st = fr.st;
    const off = { x: 0, y: 0, z: 0 };
    if (st === 8 || st === 11) {
      const k = this.drawerKinematics(obj);
      off[k.axis] = k.dist * k.sign * fd;
    } else if (st === 6 || st === 7) {
      const ax = r.wx >= r.dz ? 'x' : 'z'; // cửa lùa: trong mặt phẳng, theo cạnh ngang dài
      off[ax] = (st === 6 ? -1 : 1) * (ax === 'x' ? r.wx : r.dz) * S * fd;
    } else if (st === 15) {
      const ax = r.wx >= r.dz ? 'x' : 'z'; // trượt sâu: theo chiều sâu (cạnh ngang dài)
      off[ax] = (ax === 'x' ? r.wx : r.dz) * S * fd;
    } else if (st === 13) {
      off.y = r.hy * S * 0.8 * fd; // piston: nâng thẳng đứng
    }
    return off;
  }

  /** Đặt lại pivot/offset cho mọi part (gọi sau khi dựng hoặc đổi motion). */
  setupAll(): void {
    this.frameCache.clear();
    for (const obj of this.objects) this.setupPart(obj);
  }

  /** Định vị pivot + offset mesh theo khung chuyển động (bản lề chung / tịnh tiến). */
  setupPart(obj: PartObject): void {
    const { part, pivot, mesh } = obj;
    pivot.rotation.set(0, 0, 0);
    const st = part.motion?.salonType ?? 0;
    const box = this.partBox(obj);
    if ((!st && !part.motion?.slideVec && !part.motion?.rotate) || !box) {
      // không hoạt náo: pivot tại tâm, mesh tại gốc
      if (box) pivot.position.set(box.cx * S, box.cy * S, box.cz * S);
      mesh.position.set(0, 0, 0);
      return;
    }
    const cx = box.cx * S;
    const cy = box.cy * S;
    const cz = box.cz * S;
    const fr = this.frame(obj);
    if (fr && (fr.rot || fr.quat)) {
      // xoay (Euler bản lề X/Y/Z HOẶC quaternion trục-world): pivot tại BẢN LỀ; mesh bù về đúng vị trí world
      pivot.position.set(fr.hinge.x, fr.hinge.y, fr.hinge.z);
      mesh.position.set(cx - fr.hinge.x, cy - fr.hinge.y, cz - fr.hinge.z);
    } else {
      // tịnh tiến: pivot tại tâm member; apply() dịch trực tiếp
      pivot.position.set(cx, cy, cz);
      mesh.position.set(0, 0, 0);
    }
    if (!this.states.has(part.id)) this.states.set(part.id, { t: 0, target: 0 });
  }

  /** Author mode: gán lại chuyển động cho 1 part (đặt về trạng thái đóng). */
  reassign(partId: number, salonType: SalonType, drawerGroup: number, flip: boolean, swingOut = false): void {
    const obj = this.objects.find((o) => o.part.id === partId);
    if (!obj) return;
    obj.part.motion = { salonType, drawerGroup, flip, swingOut };
    const s = this.ensure(partId);
    s.t = 0;
    s.target = 0;
    // nhóm/đại diện có thể đổi cho nhiều part → dựng lại toàn bộ pivot
    this.setupAll();
    this.requestAnimate();
  }

  /**
   * Động học trượt ngăn kéo — suy từ HÌNH HỌC, KHÔNG hardcode trục Z (tủ chữ L có
   * dãy mở theo X). Tính từ các BOX thân/mặt trong nhóm nên mọi member (kể cả tay nắm) trượt đồng trục.
   *  - axis: trục NGANG mỏng nhất của MẶT — pháp tuyến mặt = phương kéo.
   *  - sign: từ thân hướng ra mặt (ra ngoài tủ) = sign(tâm mặt − tâm thân) theo axis.
   *  - dist: độ sâu thân ĐO THEO axis × 0.72.
   *
   * ⚠ Có model mà BAZIS gán MẶT ngăn kéo = st=8 (KHÔNG có part st=11 nào). Khi đó dùng tấm
   * ĐỨNG lớn nhất trong nhóm làm "mặt" đại diện thay cho fronts — KHÔNG được lấy `bodies[0]`
   * tuỳ tiện (tấm vách sau mỏng-theo-Z nhưng rộng-theo-X sẽ chọn nhầm trục X → kéo sang phải).
   *
   * ⚠ Mặt ngăn kéo có thể là MESH (vd "NK MẶT" xoi V45), không phải box. Nếu chỉ xét box thì
   * mặt mesh bị bỏ qua, còn lại 2 vách trước/sau ĐỐI XỨNG → reduce chọn nhầm vách sau → kéo
   * NGƯỢC (thụt vào). Vì vậy xét AABB của MỌI member (partBox, kể cả mesh) để mặt-diện-tích-lớn
   * được nhận đúng.
   */
  private drawerKinematics(obj: PartObject): { axis: 'x' | 'z'; sign: number; dist: number } {
    const grp = obj.part.motion?.drawerGroup ?? 0;
    const fronts: BoxLike[] = [];
    const bodies: BoxLike[] = [];
    const members =
      grp > 0 ? this.objects.filter((o) => o.part.motion?.drawerGroup === grp) : [obj];
    for (const o of members) {
      const b = this.partBox(o); // AABB (mm) — mặt mesh cũng vào đây, không riêng box
      if (!b) continue;
      if (o.part.motion?.salonType === 11) fronts.push(b);
      else if (o.part.motion?.salonType === 8) bodies.push(b);
    }

    // "Mặt" đại diện quyết định pháp tuyến kéo. Không có st=11 → tấm ĐỨNG lớn nhất
    // trong thân (loại tấm phẳng đáy/nóc: mỏng nhất theo Y) đóng vai mặt ngăn kéo.
    const vol = (g: BoxLike) => g.wx * g.hy * g.dz;
    let noFrontFace: BoxLike | null = null;
    let axis: 'x' | 'z' = 'z';
    if (fronts.length) {
      axis = fronts[0].wx <= fronts[0].dz ? 'x' : 'z';
    } else if (bodies.length) {
      const standing = bodies.filter((g) => Math.min(g.wx, g.hy, g.dz) !== g.hy);
      const pool = standing.length ? standing : bodies;
      noFrontFace = pool.reduce((b, g) => (vol(b) >= vol(g) ? b : g));
      axis = noFrontFace.wx <= noFrontFace.dz ? 'x' : 'z';
    } else {
      const self = this.partBox(obj);
      if (self) axis = self.wx >= self.dz ? 'x' : 'z';
    }

    const along = (g: BoxLike) => (axis === 'x' ? g.cx : g.cz);
    const depthOf = (g: BoxLike) => (axis === 'x' ? g.wx : g.dz);

    let sign = 1;
    if (fronts.length && bodies.length) {
      const fc = fronts.reduce((s, g) => s + along(g), 0) / fronts.length;
      const bc = bodies.reduce((s, g) => s + along(g), 0) / bodies.length;
      sign = Math.sign(fc - bc) || 1;
    } else if (noFrontFace) {
      // chiều = từ tâm các tấm thân còn lại hướng ra mặt đại diện
      const others = bodies.filter((g) => g !== noFrontFace);
      const ref = others.length ? others : bodies;
      const bc = ref.reduce((s, g) => s + along(g), 0) / ref.length;
      sign = Math.sign(along(noFrontFace) - bc) || 1;
    }

    let maxDepth = 0;
    for (const g of bodies) maxDepth = Math.max(maxDepth, depthOf(g));
    const self = this.partBox(obj);
    if (maxDepth < 1 && self) maxDepth = Math.max(self.wx, self.dz);
    return { axis, sign, dist: maxDepth * S * 0.72 };
  }

  /** Mở/đóng 1 part (đồng bộ cả nhóm). */
  toggle(partId: number): void {
    const obj = this.objects.find((o) => o.part.id === partId);
    if (!obj || !isAnimatable(obj.part)) return;
    const cur = this.states.get(partId)?.target ?? 0;
    const next = cur > 0.5 ? 0 : 1;
    this.setGroupTarget(obj.part, next);
    this.requestAnimate();
  }

  openAll(): void {
    this.setAllTargets(1);
  }
  closeAll(): void {
    this.setAllTargets(0);
  }

  /** Author: mở hé nhóm của part rồi TỰ ĐÓNG (~1.1s) — xem NGAY chiều mở sau khi gán/đảo (WO-04a §5.1). */
  private previewTimer?: ReturnType<typeof setTimeout>;
  previewOpen(partId: number): void {
    const obj = this.objects.find((o) => o.part.id === partId);
    if (!obj || !isAnimatable(obj.part)) return;
    this.setGroupTarget(obj.part, 1);
    this.requestAnimate();
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => {
      this.setGroupTarget(obj.part, 0);
      this.requestAnimate();
    }, 1100);
  }

  private setAllTargets(target: number): void {
    for (const { part } of this.objects) {
      if (isAnimatable(part)) this.ensure(part.id).target = target;
    }
    this.requestAnimate();
  }

  private setGroupTarget(part: WvmPart, target: number): void {
    const grp = part.motion?.drawerGroup ?? 0;
    if (grp > 0) {
      for (const { part: q } of this.objects) {
        if (q.motion?.drawerGroup === grp && this.states.has(q.id)) {
          this.ensure(q.id).target = target;
        }
      }
    } else {
      this.ensure(part.id).target = target;
    }
  }

  private ensure(id: number): AnimState {
    let s = this.states.get(id);
    if (!s) {
      s = { t: 0, target: 0 };
      this.states.set(id, s);
    }
    return s;
  }

  /** true nếu part đang mở (target > 0.5) — cho UI hiển thị. */
  isOpen(partId: number): boolean {
    return (this.states.get(partId)?.target ?? 0) > 0.5;
  }

  /** Animator: lerp & áp transform. Trả true nếu còn part chưa tới đích. */
  update = (): boolean => {
    let active = false;
    for (const obj of this.objects) {
      const s = this.states.get(obj.part.id);
      if (!s) continue;
      s.t += (s.target - s.t) * 0.07;
      if (Math.abs(s.target - s.t) > SETTLE) active = true;
      else s.t = s.target;
      this.apply(obj, s.t);
    }
    return active;
  };

  private apply(obj: PartObject, t: number): void {
    const { part, pivot, mesh } = obj;
    if (!part.motion?.salonType && !part.motion?.slideVec && !part.motion?.rotate) return;
    const fr = this.frame(obj);
    const box = this.partBox(obj);
    if (!fr || !box) return;
    const cx = box.cx * S;
    const cy = box.cy * S;
    const cz = box.cz * S;
    if (fr.quat) {
      // xoay quanh TRỤC WORLD tuỳ ý qua pivot (quaternion) — phụ kiện lật (kệ giày) / Blum HF
      mesh.position.set(cx - fr.hinge.x, cy - fr.hinge.y, cz - fr.hinge.z);
      this._axis.set(fr.quat.ax, fr.quat.ay, fr.quat.az);
      pivot.quaternion.setFromAxisAngle(this._axis, fr.quat.angle * t);
      if (fr.bi) {
        // cánh dưới Blum HF: bản lề giữa di động = H_M0 + R_A(angleUp·t)·v0 − v0, v0 = (0,−Lup,0)
        this._tmpQ.setFromAxisAngle(this._axis.set(fr.bi.ax, fr.bi.ay, fr.bi.az), fr.bi.angleUp * t);
        this._tmpV.set(0, -fr.bi.Lup, 0).applyQuaternion(this._tmpQ);
        pivot.position.set(fr.bi.hm0x + this._tmpV.x, fr.bi.hm0y + this._tmpV.y + fr.bi.Lup, fr.bi.hm0z + this._tmpV.z);
      } else {
        pivot.position.set(fr.hinge.x, fr.hinge.y, fr.hinge.z);
      }
    } else if (fr.rot) {
      // xoay quanh bản lề chung của nhóm
      pivot.position.set(fr.hinge.x, fr.hinge.y, fr.hinge.z);
      mesh.position.set(cx - fr.hinge.x, cy - fr.hinge.y, cz - fr.hinge.z);
      const a = fr.rot.angle * t;
      pivot.rotation.set(
        fr.rot.axis === 'x' ? a : 0,
        fr.rot.axis === 'y' ? a : 0,
        fr.rot.axis === 'z' ? a : 0,
      );
    } else {
      // tịnh tiến rigid: tâm member + vector nhóm (đã cache trong frame)
      pivot.rotation.set(0, 0, 0);
      mesh.position.set(0, 0, 0);
      const off = fr.off ?? { x: 0, y: 0, z: 0 };
      pivot.position.set(cx + off.x * t, cy + off.y * t, cz + off.z * t);
    }
  }
}
